import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import IconButton, { IconButtonProps } from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';

import { loadApplicationSourceIssues } from '../../application-source-issue/actions/load-application-source-issues';
import {
  applicationSourceIssuesForJobIdSelector,
  applicationSourceIssuesStateForOrgIdSelector,
} from '../../application-source-issue/application-source-issue-selectors';
import { jobsStateForIdSelector, jobsStateForOrgIdSelector } from '../../reducers/job-selectors';
import { orgSelector } from '../../reducers/org-selectors';

import { ApplicantSourceStatusDialog } from './applicant-source-status-dialog';
import { ApplicantSourceStatusIcon } from './applicant-source-status-icon';

import { isActiveStatus, JOB_STATUSES } from '../../util/job-status';

export enum SourceStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  NA = 'NA',
  INACTIVE = 'INACTIVE',
  NOT_PUBLISHED = 'NOT_PUBLISHED',
}

const useStyles = makeStyles({
  root: {
    '&.Mui-disabled': {
      pointerEvents: 'auto',
    },
  },
});

const mapStateToProps = (state: any, { job, orgId }: InputProps) => ({
  applicationSourceIssues: applicationSourceIssuesForJobIdSelector(state, job.job_id),
  applicationSourceIssuesState: applicationSourceIssuesStateForOrgIdSelector(state, orgId),
  jobState: jobsStateForIdSelector(state, job.job_id),
  jobsByOrgIdState: jobsStateForOrgIdSelector(state, orgId),
  org: orgSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadApplicationSourceIssues: () => dispatch(loadApplicationSourceIssues(orgId)),
  },
});

type InputProps = {
  job: Job;
  orgId: number;
  iconEdge?: IconButtonProps['edge'];
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const ApplicantSourceStatus = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    actions,
    job,
    applicationSourceIssuesState,
    jobState,
    jobsByOrgIdState,
    applicationSourceIssues,
    iconEdge,
    org,
  }: Props) => {
    const { NEEDS_REVIEW, PENDING, SUCCESS, INACTIVE, NOT_PUBLISHED } = SourceStatus;

    const classes = useStyles();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);

    const jobIsLoading =
      (jobState && jobState.loading) || (jobsByOrgIdState && jobsByOrgIdState.loading);
    const loading =
      !applicationSourceIssuesState || applicationSourceIssuesState.loading || jobIsLoading;
    const unresolvedIssues = applicationSourceIssues.filter((issue) => !issue.resolvedAt);
    const isInactive = !isActiveStatus(job.status);

    useEffect(() => {
      actions.loadApplicationSourceIssues();
    });

    let status: SourceStatus | null = null;
    if (!loading) {
      let mostRecentEdit = null;
      const jobEditMoments = [];
      if (job.lastActivatedAt) {
        jobEditMoments.push(moment(job.lastActivatedAt));
      }
      if (job.lastUpdatedAt) {
        jobEditMoments.push(moment(job.lastUpdatedAt));
      }
      if (jobEditMoments.length) {
        mostRecentEdit = moment(moment.max(jobEditMoments));
      }

      // We use a 12 hour buffer when the job has just been created or was just turned on
      // because Indeed takes up to 6 hours to index our jobs and that doesn't
      // include the review process. Also, we can't know when Indeed has
      // actually hit a problem so indeed-view-issue-watcher-job.js
      // creates a new INDEED_NO_VIEWS issue after 1 day.
      const isWithinBufferPeriod = moment().isSameOrBefore(moment(mostRecentEdit).add(12, 'hours'));

      let hasPostingError = false;
      if (unresolvedIssues.length) {
        const mostRecentIssueDate = moment.max(
          unresolvedIssues.map((issue) => moment(issue.createdAt)),
        );
        const hasIssueAfterEdit = mostRecentIssueDate.isSameOrAfter(mostRecentEdit);

        hasPostingError = !isWithinBufferPeriod || hasIssueAfterEdit;
      }

      if (isInactive) {
        status = INACTIVE;
      } else if (job.status === JOB_STATUSES.ACTIVE) {
        status = NOT_PUBLISHED;
      } else if (hasPostingError) {
        status = NEEDS_REVIEW;
      } else if (isWithinBufferPeriod) {
        status = PENDING;
      } else {
        status = SUCCESS;
      }
    }

    const handleClick = () => {
      setDialogOpen(true);
    };

    const handleClose = () => {
      setDialogOpen(false);
    };

    const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
    };

    return (
      <div
        className="u-display-flex u-alignItems-center"
        role="presentation"
        onClick={stopPropagation}
      >
        <IconButton
          edge={iconEdge}
          disabled={loading || job.status !== JOB_STATUSES.PUBLISHED}
          onClick={handleClick}
          className={`u-padding-md u-margin-left-md-neg ${classes.root}`}
        >
          <ApplicantSourceStatusIcon status={status} />
        </IconButton>
        {job.status === JOB_STATUSES.PUBLISHED && dialogOpen && (
          <ApplicantSourceStatusDialog
            org={org}
            job={job}
            sourceIssues={unresolvedIssues}
            status={status}
            onClose={handleClose}
          />
        )}
      </div>
    );
  },
);
